// The endpoints this app calls. All are Lambda Function URLs owned by
// Longview-Api; this repo has no backend of its own.
//
// These are build-time values baked into the bundle, so they are public URLs
// and nothing else. No credential is ever compiled in: the Deepgram and
// Anthropic keys live in Secrets Manager and are only ever read by the Lambdas.

/**
 * Trimmed, because these are pasted into GitHub environment variables by hand
 * and a trailing newline comes with the paste more often than not. An untrimmed
 * value survives every check here - it is a non-empty string, and it even looks
 * right in a log - and then quietly defeats trailing-slash normalisation,
 * producing a URL with a doubled slash. That cost a CORS-shaped debugging
 * session once already.
 */
function url(value: string | undefined): string {
  return (value ?? '').trim();
}

const sttTokenUrl = url(import.meta.env.VITE_STT_TOKEN_URL);
const extractUrl = url(import.meta.env.VITE_EXTRACT_URL);
const liveSwitchTokenUrl = url(import.meta.env.VITE_LIVESWITCH_TOKEN_URL);
const loginUrl = url(import.meta.env.VITE_LOGIN_API_URL);
const checklistsUrl = url(import.meta.env.VITE_CHECKLISTS_URL);
const inspectionsUrl = url(import.meta.env.VITE_INSPECTIONS_URL);
const presenceUrl = url(import.meta.env.VITE_PRESENCE_URL);

export const endpoints = {
  /** POST -> a short-lived Deepgram token plus the socket URL it authorizes. */
  sttToken: sttTokenUrl,
  /** POST -> one complete extraction pass. */
  extract: extractUrl,
  /** POST -> a LiveSwitch register token and the identity it was signed for. */
  liveSwitchToken: liveSwitchTokenUrl,
  /** Base URL of Longview-Api's LoginStack. Staff sign-in only. */
  login: loginUrl,
  /** Base URL of /checklists CRUD. The only endpoint here that stores anything. */
  checklists: checklistsUrl,
  /** Base URL of /inspections. Previous calls and their recordings; staff only. */
  inspections: inspectionsUrl,
  /** Base URL of /presence. Available / busy / away, shared with other apps. */
  presence: presenceUrl,
};

export type EndpointProblem = { endpoint: string; envVar: string };

/** Missing endpoints are a deploy-time mistake, so say which one plainly. */
export function missingEndpoints(): EndpointProblem[] {
  const problems: EndpointProblem[] = [];
  if (!endpoints.sttToken) {
    problems.push({ endpoint: 'transcription token', envVar: 'VITE_STT_TOKEN_URL' });
  }
  if (!endpoints.extract) {
    problems.push({ endpoint: 'extraction', envVar: 'VITE_EXTRACT_URL' });
  }
  if (!endpoints.liveSwitchToken) {
    problems.push({ endpoint: 'video call', envVar: 'VITE_LIVESWITCH_TOKEN_URL' });
  }
  if (!endpoints.login) {
    problems.push({ endpoint: 'sign-in', envVar: 'VITE_LOGIN_API_URL' });
  }
  if (!endpoints.checklists) {
    problems.push({ endpoint: 'checklist storage', envVar: 'VITE_CHECKLISTS_URL' });
  }
  if (!endpoints.inspections) {
    problems.push({ endpoint: 'previous calls', envVar: 'VITE_INSPECTIONS_URL' });
  }
  if (!endpoints.presence) {
    problems.push({ endpoint: 'status', envVar: 'VITE_PRESENCE_URL' });
  }
  return problems;
}
